import Card from '@/main/components/shared/card/card';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputAmountControlled from '@/main/components/shared/inputText/inputAmountControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import { PreviousItemAnswersResponse } from '@/main/models/response';
import {
  ReassignForm,
  ReassignPartData
} from '@/main/screens/order/reassignOrder/reassignScreenUtils';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import { getTranslateByKey, isNullUndefinedEmpty } from '@/main/services/utils';
import { useVisualIdentityReducer } from '@/main/store/visualIdentity';
import { Add, Close, Warning } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import * as S from './styles';

import RowLabel from '@/main/components/common/rowLabel/rowLabel';
import { KeyValueDefault } from '@/main/models/common';
import {
  ActionsCol,
  DeadlineCol,
  PartTypeCol,
  PriceCol,
  ReasonCol,
  ReceivedCol,
  SupplierCol,
  SupplierPartCol
} from '@/main/screens/order/reassignOrder/components/stylesColumns';
import ReasonCell from '@/main/screens/order/reassignOrder/components/supplierOptionRow/reasonCell';

interface ISupplierOptionRow {
  partIndex: number;
  part: ReassignPartData;
  supplierItem: PreviousItemAnswersResponse;
  supplierIndex: number;
  isSelected: boolean;
  partSelectedGuid: string | null;
  isFirstRow: boolean;
  isDirectAssignLoading: boolean;
  onSelect: (supplierItem: PreviousItemAnswersResponse) => void;
  onToggleExpand?: () => void;
  isExpanded?: boolean;
  isDirectAssignActive: boolean;
  onToggleDirectAssign: () => void;
  collapseIcon?: React.ReactNode;
  partTypeOptions: KeyValueDefault[];
  showEditControl: boolean;
  showPrices: boolean;
}

const ID_BASE = 'id-reassignOrder';

const InputWrapper = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{ width: '100%' }}
    onClick={e => e.stopPropagation()}
    onMouseDown={e => e.stopPropagation()}>
    {children}
  </div>
);
function SupplierOptionRow({
  isFirstRow,
  isSelected,
  partSelectedGuid,
  onSelect,
  part,
  partIndex,
  supplierItem,
  supplierIndex,
  isDirectAssignActive,
  onToggleDirectAssign,
  onToggleExpand,
  collapseIcon,
  isExpanded,
  partTypeOptions,
  showEditControl,
  isDirectAssignLoading,
  showPrices
}: Readonly<ISupplierOptionRow>) {
  const { control, setValue } = useFormContext<ReassignForm>();

  const partData = part;

  const { visualIdentity: visualIdentityReducer } = useVisualIdentityReducer();

  const fieldBase =
    `parts.${partIndex}.supplierItems.${supplierIndex}` as const;

  const rowId = `part-${partIndex}-supplier-${supplierItem.guidAnsweredQuoteSupplierItem}`;

  const canEdit = isSelected && showEditControl;

  const primaryGuid =
    part.itemAnswers.find(a => a.isOriginalSelected)
      ?.guidAnsweredQuoteSupplierItem ?? null;
  const hasCollapse = part.itemAnswers.length > 1;

  const selectedIsNotPrimary =
    partSelectedGuid !== null && partSelectedGuid !== primaryGuid;

  const showSelectedBadge =
    isFirstRow && hasCollapse && !isExpanded && selectedIsNotPrimary;

  const hasAnyDecision = partSelectedGuid !== null || isDirectAssignActive;

  const supplierPartText = [
    supplierItem.partDescription,
    supplierItem.partNumber
  ]
    .filter(Boolean)
    .join(' - ');

  const renderPartType = () => {
    const quoteProcurementPartInfoData = partData?.quoteProcurementPartInfo;
    const isTireGlass =
      quoteProcurementPartInfoData?.idPartTypeQuoteItem == PartTypeEnum.Tire ||
      quoteProcurementPartInfoData?.idPartTypeQuoteItem == PartTypeEnum.Glass;

    const titleTireGlass = [
      quoteProcurementPartInfoData?.quoteItemDescription,
      quoteProcurementPartInfoData?.notes
        ? quoteProcurementPartInfoData.notes
        : quoteProcurementPartInfoData?.partTypeQuoteItem
    ]
      .filter(Boolean)
      .join(' - ');

    if (isTireGlass && !!quoteProcurementPartInfoData?.notes) {
      return (
        <TooltipComponent
          title={
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Box
                display="flex"
                overflow="hidden"
                alignItems="left"
                flexDirection="column"
                minWidth={0}>
                <div>
                  <span
                    style={{
                      fontWeight: 'bold'
                    }}>
                    {`${quoteProcurementPartInfoData.quoteItemDescription} [${quoteProcurementPartInfoData.notes}]`}
                  </span>

                  <Warning
                    style={{
                      color: visualIdentityReducer.colors.orange,
                      flexShrink: 0
                    }}
                    fontSize="small"
                  />
                </div>
                <span>
                  {!isNullUndefinedEmpty(quoteProcurementPartInfoData.glassInfo)
                    ? quoteProcurementPartInfoData.glassInfo
                    : ''}
                  {!isNullUndefinedEmpty(quoteProcurementPartInfoData.tireInfo)
                    ? quoteProcurementPartInfoData.tireInfo
                    : ''}
                </span>
              </Box>
              <div>
                <RowLabel
                  label={getTranslateByKey('information.claimNumber')}
                  content={partData.claimNumber}
                />
                <RowLabel
                  label={getTranslateByKey('information.order')}
                  content={partData.procurementCode}
                />
                <RowLabel
                  label={getTranslateByKey('information.quote')}
                  content={partData.quoteCode}
                />
                <RowLabel
                  label={getTranslateByKey('information.insurer')}
                  content={partData.insurerName}
                />
                <RowLabel
                  label={getTranslateByKey('information.vehicle')}
                  content={partData.vehicleFullName}
                />
              </div>
            </div>
          }>
          <Box
            display="flex"
            overflow="hidden"
            alignItems="center"
            minWidth={0}>
            <S.TextGrid>{titleTireGlass}</S.TextGrid>
            <Warning
              style={{
                color: visualIdentityReducer.colors.orange,
                flexShrink: 0
              }}
              fontSize="small"
            />
          </Box>
        </TooltipComponent>
      );
    } else {
      return (
        <TooltipComponent
          title={
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <span style={{ fontWeight: 'bold' }}>
                {isTireGlass ? (
                  titleTireGlass
                ) : (
                  <>
                    <strong>
                      {part.quoteProcurementPartInfo.quoteItemDescription}
                    </strong>
                    {!isNullUndefinedEmpty(
                      part.quoteProcurementPartInfo.quoteItemPartNumber
                    ) && (
                      <span style={{ fontWeight: 'normal' }}>
                        {' - '}
                        <span>
                          {part.quoteProcurementPartInfo.quoteItemPartNumber}
                        </span>
                      </span>
                    )}
                  </>
                )}
              </span>
              <div>
                <RowLabel
                  label={getTranslateByKey('information.claimNumber')}
                  content={partData.claimNumber}
                />
                <RowLabel
                  label={getTranslateByKey('information.order')}
                  content={partData.procurementCode}
                />
                <RowLabel
                  label={getTranslateByKey('information.quote')}
                  content={partData.quoteCode}
                />
                <RowLabel
                  label={getTranslateByKey('information.insurer')}
                  content={partData.insurerName}
                />
                <RowLabel
                  label={getTranslateByKey('information.vehicle')}
                  content={partData.vehicleFullName}
                />
              </div>
            </div>
          }>
          <S.TextGrid>
            {isTireGlass ? (
              titleTireGlass
            ) : (
              <>
                <strong>
                  {part.quoteProcurementPartInfo.quoteItemDescription}
                </strong>
                {!isNullUndefinedEmpty(
                  part.quoteProcurementPartInfo.quoteItemPartNumber
                ) && (
                  <span style={{ fontWeight: 'normal' }}>
                    {' - '}
                    <span>
                      {part.quoteProcurementPartInfo.quoteItemPartNumber}
                    </span>
                  </span>
                )}
              </>
            )}
          </S.TextGrid>
        </TooltipComponent>
      );
    }
  };

  return (
    <Card
      id={`${ID_BASE}-card-${supplierIndex}`}
      className="box-layout-card-collumn"
      borderColor={
        isSelected ? visualIdentityReducer.colors.pantone2726C : undefined
      }
      onClick={() => onSelect(supplierItem)}
      style={{
        position: 'relative',
        minHeight: '80px',
        width: '100%',
        padding: '0'
      }}>
      <S.ContainerCardShoppingRow active={true}>
        {showSelectedBadge && <S.BoxBadge>1</S.BoxBadge>}
        <ReceivedCol>{renderPartType()}</ReceivedCol>
        <SupplierPartCol>
          <TooltipComponent title={supplierPartText}>
            <S.TextGrid>{supplierPartText}</S.TextGrid>
          </TooltipComponent>
        </SupplierPartCol>
        <SupplierCol>
          <TooltipComponent title={supplierItem.supplierName}>
            <S.TextGrid>{supplierItem.supplierName}</S.TextGrid>
          </TooltipComponent>
        </SupplierCol>

        <PartTypeCol>
          <InputWrapper>
            <KogAutoCompleteControlled
              id={`${ID_BASE}-select-partType-${rowId}`}
              nameCustom={`${fieldBase}.partType`}
              label={getTranslateByKey('information.partTypes')}
              control={control}
              setValueCustom={setValue}
              options={partTypeOptions}
              isLoading={false}
              onChangeCustom={() => {}}
              disabled={!canEdit}
              required={isSelected}
              rulesCustom={{ required: isSelected }}
            />
          </InputWrapper>
        </PartTypeCol>

        <DeadlineCol>
          <InputWrapper>
            <KogInputTextControlledNew
              id={`${ID_BASE}-input-deliveryDeadline-${rowId}`}
              name={`${fieldBase}.deliveryDeadline`}
              control={control}
              label={getTranslateByKey('information.deliveryDeadline')}
              onChangeCustom={() => {}}
              disabled={!canEdit}
              required={isSelected}
              hasOnlyNumber
              hasOnlyNumberInteger
            />
          </InputWrapper>
        </DeadlineCol>
        {showPrices && (
          <PriceCol>
            <InputWrapper>
              <KogInputAmountControlled
                typeInput="filled"
                id={`${ID_BASE}-input-price-${rowId}`}
                name={`${fieldBase}.price`}
                label={getTranslateByKey('information.price')}
                control={control}
                onClickCustom={e => e.stopPropagation()}
                disabled={!canEdit}
                required={isSelected}
              />
            </InputWrapper>
          </PriceCol>
        )}

        <ReasonCol>
          {isFirstRow && (
            <ReasonCell partIndex={partIndex} hasAnyDecision={hasAnyDecision} />
          )}
        </ReasonCol>

        <ActionsCol>
          {isFirstRow &&
            (isDirectAssignActive ? (
              <Tooltip
                title={getTranslateByKey(
                  'information.clickHereToDiscardNewSupplier'
                )}>
                <IconButton
                  id={`${ID_BASE}-btn-removeDirectAssign-${partIndex}`}
                  color="secondary"
                  loading={isDirectAssignLoading}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onToggleDirectAssign();
                  }}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip
                title={getTranslateByKey(
                  'information.clickHereToAddNewSupplier'
                )}>
                <IconButton
                  id={`${ID_BASE}-btn-addDirectAssign-${partIndex}`}
                  color="primary"
                  size="small"
                  loading={isDirectAssignLoading}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onToggleDirectAssign();
                  }}>
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}

          {collapseIcon && (
            <IconButton
              size="small"
              id={`${ID_BASE}-btn-toggleCollapse-${partIndex}`}
              onClick={e => {
                e.stopPropagation();
                onToggleExpand?.();
              }}>
              {collapseIcon}
            </IconButton>
          )}
        </ActionsCol>
      </S.ContainerCardShoppingRow>
    </Card>
  );
}

export default React.memo(SupplierOptionRow);
